import { ReactElement, useMemo, useState } from 'react';
import { Checkbox, ImageUpload, Input, Typography } from '@weareopenr/component-library';
import { FormAutocomplete } from 'src/components/FormElements';
import { Controller, UseFormReturn } from 'react-hook-form';
import { ItemType, NO_INVENTORY_TYPES } from 'src/constants';
import { getInventoryOptions, isPLUpgradeBrand } from 'src/helpers';
import { IGlobalItem } from '../CreateGlobalItemWizard/types';
import styles from './GlobalItemDetailsFields.module.scss';
import { useInventory } from 'src/hooks/useInventory';
import { FormVariant } from '../../types/menuItemForm.types';
import { ProductRangeSelector } from '../ProductRangeSelector/ProductRangeSelector';
import { useRanges } from '../../hooks/useRanges/useRanges';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { InfoTooltip, InfoTooltipType } from '../InfoTooltip/InfoTooltip';

export interface IMenuItemDetailsProps {
  itemType: IGlobalItem['type'];
  formFields: UseFormReturn<IGlobalItem>;
  isProductSynced?: boolean;
  variant?: FormVariant;
  showImageField?: boolean;
  canShowEnhancedBundleFlag?: boolean;
}

const GlobalItemDetailsFields = ({
  itemType,
  formFields,
  isProductSynced = false,
  variant,
  showImageField = false,
  canShowEnhancedBundleFlag = false,
}: IMenuItemDetailsProps): ReactElement => {
  const {
    register,
    formState: { errors },
    control,
    setValue,
    clearErrors,
    watch,
  } = formFields;

  const [optionGroupSearchTerm, setOptionGroupSearchTerm] = useState<string>();
  const searchQuery = useDebounce(optionGroupSearchTerm);
  const { data: recipes, hasNextPage, fetchNextPage } = useInventory(15, searchQuery);
  const isV2 = isPLUpgradeBrand();
  const { data: ranges } = useRanges({ enabled: itemType === ItemType.BUNDLE && variant === FormVariant.CREATE });

  const loadMoreRecipes = () => {
    if (hasNextPage) fetchNextPage();
  };

  const recipeOptions = useMemo(() => {
    return getInventoryOptions(recipes?.pages?.flatMap((page) => page.data) || []);
  }, [recipes]);
  const watchRecipe = watch('recipe_id');
  return (
    <div className={styles['input-wrapper']}>
      <Input
        type="text"
        label="Name"
        register={register('name', { required: 'Please enter a product name' })}
        error={errors?.name?.message}
        disabled={isProductSynced}
      />

      {!NO_INVENTORY_TYPES.includes(itemType) && !isProductSynced && (
        <FormAutocomplete
          onLoadMore={loadMoreRecipes}
          setSearchTerm={setOptionGroupSearchTerm}
          name="recipe_id"
          label="Recipe"
          control={control}
          testId="autocomplete-recipe"
          options={recipeOptions}
          disabled={isProductSynced}
        />
      )}
      <Input type="text" label="Consumer name" register={register('consumer_name')} />
      <Input type="text" label="Description" rows={2} register={register('description')} />

      {itemType === ItemType.BUNDLE && variant === FormVariant.CREATE && isV2 && (
        <ProductRangeSelector
          ranges={ranges}
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
          watch={watch}
          clearErrors={clearErrors}
          productType={itemType}
        />
      )}

      <Input type="text" label="Default POS ID" register={register('plu')} />

      {itemType === ItemType.BUNDLE && (
        <Input
          register={register('party_size', {
            setValueAs: (v) => (v == null || v === '' ? null : Number(v)),
            min: { value: 1, message: 'Party size must be 1 or more' },
          })}
          label="Party size"
          type="number"
          error={errors?.party_size?.message}
        />
      )}

      {showImageField && (
        <Controller
          control={control}
          name="image"
          render={({ field: { value, onChange } }) => {
            return (
              <ImageUpload
                title="Image"
                prompt="Upload Image"
                image={value}
                setImage={(value: File | string | null) => onChange(value)}
                shouldDeleteImage={isV2}
              />
            );
          }}
        />
      )}

      <Typography size="medium" weight="semi-bold">
        Product Declaration Details
      </Typography>
      <div className={styles['checkbox-container']}>
        <Checkbox
          label="Age Verification Required?"
          name="age_verification"
          labelWeight="regular"
          register={register}
        />
        {!NO_INVENTORY_TYPES.includes(itemType) && watchRecipe && (
          <div className={styles['has-tooltip']}>
            <Checkbox
              register={register}
              label="Contains High Fat, Salt or Sugar"
              name="is_hfss"
              labelWeight="regular"
            />
            <InfoTooltip type={InfoTooltipType.HFSS} />
          </div>
        )}
        {itemType === ItemType.BUNDLE && canShowEnhancedBundleFlag && (
          <div className={styles['has-tooltip']}>
            <Checkbox
              register={register}
              label="Enhanced bundles flag"
              name="is_enhanced_bundle"
              labelWeight="regular"
            />
            <InfoTooltip type={InfoTooltipType.ENHANCED_BUNDLE} />
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalItemDetailsFields;
